import React from "react";
import {
  Box, Typography, Button,
  Dialog, DialogTitle, DialogContent, DialogActions
} from "@mui/material";
import { CheckCircleOutline } from "@mui/icons-material";

const SuccessModal = ({ open, onClose, onReset, successDetails }) => {
  const [showFailed, setShowFailed] = React.useState(false);

  React.useEffect(() => {
    if (!open) setShowFailed(false);
  }, [open]);

  let failedNotes = [];
  if (successDetails.details) {
    if (successDetails.mode === "MULTIPLE") {
      successDetails.details.forEach(sec => {
        if (sec.cloned && sec.cloned.length > 0) {
          failedNotes = [...failedNotes, ...sec.cloned.filter(n => !n.success)];
        }
        if (sec.error) {
          failedNotes.push({
            titulo: sec.originSectionId !== -1 
              ? `Fallo en Sección Origen (ID: ${sec.originSectionId})`
              : "Fallo general de Sección",
            originalId: "N/A",
            error: sec.error
          });
        }
      });
    } else {
      failedNotes = successDetails.details.filter(n => !n.success);
    }
  }

  return (
    <Dialog
      open={open}
      onClose={onClose}
      PaperProps={{ sx: { borderRadius: 3, p: 1, maxWidth: "550px" } }}
    >
      <DialogTitle sx={{ display: "flex", alignItems: "center", gap: 1.5, fontWeight: 700, color: "success.main" }}>
        <CheckCircleOutline /> Clonación Realizada
      </DialogTitle>

      <DialogContent dividers>
        {successDetails.mode === "COINCIDENCIA" ? (
          <>
            <Typography variant="body1" gutterBottom>
              Se clonaron <strong>{successDetails.sections?.length || 0}</strong> sección(es) al documento{" "}
              <strong>{successDetails.document}</strong>:
            </Typography>
            <Box sx={{ bgcolor: "grey.100", p: 1.5, borderRadius: 2, mt: 1, maxHeight: "180px", overflowY: "auto" }}>
              {successDetails.sections?.length > 0 ? (
                successDetails.sections.map((section) => (
                  <Typography
                    key={section.id}
                    variant="body2"
                    sx={{ py: 0.5, borderBottom: "1px solid", borderColor: "grey.200", "&:last-child": { border: 0 } }}
                  >
                    • {section.name}
                  </Typography>
                ))
              ) : (
                <Typography variant="caption" sx={{ color: "text.secondary" }}>
                  Sin secciones registradas
                </Typography>
              )}
            </Box>
          </>
        ) : successDetails.mode === "NOTAS" ? (
          <>
            <Typography variant="body1" gutterBottom>
              Se han clonado las notas:
            </Typography>
            <Box sx={{ bgcolor: "grey.100", p: 1.5, borderRadius: 2, mb: 2, maxHeight: "100px", overflowY: "auto" }}>
              <Typography
                variant="caption"
                sx={{ fontFamily: "monospace", color: "text.secondary", wordBreak: "break-all" }}
              >
                {successDetails.notes.join(", ")}
              </Typography>
            </Box>
            <Typography variant="body2">
              A la sección <strong>{successDetails.sectionTarget}</strong> del documento{" "}
              <strong>{successDetails.document}</strong> con éxito.
            </Typography>
          </>
        ) : successDetails.mode === "MULTIPLE" ? (
          <>
            <Typography variant="body1" gutterBottom>
              Clonación múltiple finalizada. Resultados:
            </Typography>
            <Box sx={{ bgcolor: "grey.100", p: 1.5, borderRadius: 2, mb: 2 }}>
              <Typography variant="body2" sx={{ mb: 0.5 }}>
                • Notas clonadas exitosamente: <strong>{successDetails.results?.totalCloned || 0}</strong>
              </Typography>
              <Typography variant="body2" sx={{ mb: 0.5 }}>
                • Notas fallidas: <strong style={{ color: "red" }}>{successDetails.results?.totalFailed || 0}</strong>
              </Typography>
              <Typography variant="body2">
                • Secciones de origen procesadas: <strong>{successDetails.results?.sectionsProcessed || 0}</strong>
              </Typography>
            </Box>
            <Typography variant="body2">
              Destino: Sección <strong>{successDetails.sectionTarget}</strong> del documento{" "}
              <strong>{successDetails.document}</strong>.
            </Typography>
          </>
        ) : (
          <Typography variant="body1" sx={{ py: 1 }}>
            Se han clonado las notas de la sección{" "}
            <strong>{successDetails.sectionOrigin}</strong> a la sección{" "}
            <strong>{successDetails.sectionTarget}</strong> del documento{" "}
            <strong>{successDetails.document}</strong> de forma exitosa.
          </Typography>
        )}

        <Box sx={{ mt: 2, pt: 2, borderTop: "1px solid", borderColor: "divider" }}>
          <Button 
            variant="outlined" 
            color={failedNotes.length > 0 ? "error" : "inherit"} 
            size="small" 
            onClick={() => setShowFailed(!showFailed)}
            disabled={failedNotes.length === 0}
            sx={{ mb: 1, textTransform: "none", borderRadius: 2 }}
          >
            {showFailed ? "Ocultar notas fallidas" : `Ver notas fallidas (${failedNotes.length})`}
          </Button>
          {showFailed && failedNotes.length > 0 && (
            <Box sx={{ bgcolor: "#fee2e2", p: 1.5, borderRadius: 2, maxHeight: "200px", overflowY: "auto" }}>
              {failedNotes.map((note, idx) => (
                <Box key={idx} sx={{ mb: 1, pb: 1, borderBottom: "1px solid", borderColor: "#fca5a5", "&:last-child": { border: 0, mb: 0, pb: 0 } }}>
                  <Typography variant="body2" sx={{ fontWeight: 600, color: "#991b1b" }}>
                    {note.titulo || "Sin título"} (ID: {note.originalId || "Desconocido"})
                  </Typography>
                  <Typography variant="caption" sx={{ color: "#b91c1c" }}>
                    Error: {note.error || "Razón desconocida"}
                  </Typography>
                </Box>
              ))}
            </Box>
          )}
        </Box>
      </DialogContent>

      <DialogActions sx={{ p: 2, justifyContent: "space-between" }}>
        <Button
          onClick={onClose}
          variant="outlined"
          color="inherit"
          sx={{ textTransform: "none" }}
        >
          Cerrar ventana
        </Button>
        <Button
          onClick={onReset}
          variant="contained"
          color="primary"
          sx={{ textTransform: "none" }}
        >
          Realizar otra clonación
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default SuccessModal;